import { datadogLogs } from '@datadog/browser-logs';
import { useEffect, useState } from 'react';
import { MAIN_SERVICE_PATHS } from '@/constants/navigation';
import { CouponIssueReason } from '../../api/user/dto/coupon-issue-reason.enum';
import type { IssuedCoupon } from '../../api/user/dto/response/app-onboarding-benefit.res.dto';
import useMarketingConsentBenefitEligibilityQuery from '../../api/user/hooks/useMarketingConsentBenefitEligibilityQuery';
import { UserApi } from '../../api/user/user.api';
import { TimeConstants } from '../../constants/time';
import { AutoShowModalId } from '../../store/useAutoShowModalStore';
import { LOCAL_STORAGE_KEYS } from '../../utils/common/local-storage-keys';
import { ReactNativeGtm } from '../../utils/gtm/react-native/ReactNativeGtm';
import useMeQuery from '../../utils/hook/react-query/user/useMeQuery';
import { useAutoShowModalSlot } from '../../utils/hook/useAutoShowModalSlot';
import useClientSide from '../../utils/hook/useClientSide';
import { useHistoryMethod } from '../../utils/hook/useHistoryMethod';
import { useModalCooldown } from '../../utils/hook/useModalCooldown';
import { usePathnameMethod } from '../../utils/hook/usePathnameMethod';
import {
  isReactNative,
  requestNotificationPermission,
} from '../../utils/react-native';

/** 마케팅 동의 프로모션 쿠폰 금액 표기 (안내 카피·이미지 alt 공용) */
export const COUPON_AMOUNT_TEXT = '2,000원';

/** 모달 닫기 시 재노출 쿨다운 기간 */
const CLOSE_COOLDOWN_DURATION_MS = TimeConstants.DAY;

/** "7일 동안 보지 않기" 클릭 시 재노출 쿨다운 기간 */
const NEVER_SHOW_COOLDOWN_DURATION_MS = 7 * TimeConstants.DAY;

/** 마케팅 수신 동의 모달 노출 단계 */
export enum MarketingConsentStep {
  /** 동의 유도 + 쿠폰 받기 */
  CONSENT = 'CONSENT',

  /** 쿠폰 발급 완료 안내 */
  COUPON_ISSUED = 'COUPON_ISSUED',
}

/** 발급 완료 후 "쿠폰 쓰러 가기" 이동 대상 — 마켓 홈 */
const MARKET_PATH = '/ustore';

/**
 * 마케팅 수신 동의 유도 팝업의 노출 조건 판정과 2단계(동의 유도 → 쿠폰 발급 완료) 전환을 관리한다.
 *
 * - 앱 전용: 앱 웹뷰 + 로그인 + 마케팅 미동의 + 띠배너 6개 탭 경로 + BE 노출 자격(eligibility: 이전 동의 이력 없음·미발급·활성 쿠폰)
 * - "동의하고 쿠폰 받기": PATCH /users/me/marketing-policy (issueCoupon=true)로 동의+조건부 발급을 한 번에 처리 →
 *   ISSUED면 발급 완료 바텀시트로 전환. 동의는 발급 성패와 무관하게 항상 반영된다
 * - OS 알림 권한 요청은 쿠폰 발급과 무관하게 동의 후 네이티브에 위임한다 (허용은 유저 선택)
 * - 발급 단계에서 슬롯이 풀리지 않도록 me 갱신을 종료 시점으로 미룬다
 * - 재노출 억제는 클라이언트 쿨다운(닫기 1일 / "7일 동안 보지 않기" 7일)으로 관리하며, 동의 완료 유저는 eligibility로 자연 제외된다
 * - 다른 자동 노출 모달과의 중첩 방지는 autoShowModal 슬롯이 담당한다
 */
export const useMarketingConsentModal = () => {
  const history = useHistoryMethod();
  const { pathname } = usePathnameMethod();

  const [step, setStep] = useState(MarketingConsentStep.CONSENT);
  const [issuedCoupon, setIssuedCoupon] = useState<IssuedCoupon | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { me, refetchMe } = useMeQuery();

  const isClientSide = useClientSide();
  const { isInCooldown, startCooldown } = useModalCooldown(
    LOCAL_STORAGE_KEYS.MODAL.MARKETING_CONSENT,
  );

  const isLoggedIn = !!me;
  const isMarketingNotAgreed = me?.marketingPolicy === false;
  const isVisiblePath = MAIN_SERVICE_PATHS.includes(pathname);

  // 노출 자격 조회 전제 조건 — 앱 웹뷰·로그인·미동의·노출 경로·쿨다운 외일 때만 BE에 자격을 확인한다
  const canCheckEligibility =
    isClientSide &&
    isReactNative() &&
    isLoggedIn &&
    isMarketingNotAgreed &&
    isVisiblePath &&
    !isInCooldown;

  const { eligible } = useMarketingConsentBenefitEligibilityQuery({
    enabled: canCheckEligibility,
  });

  const enabled = canCheckEligibility && eligible;

  const { isActive } = useAutoShowModalSlot({
    id: AutoShowModalId.MARKETING_CONSENT,
    enabled,
  });

  const agreeMarketingAndIssueCoupon = async () => {
    // 중복 제출 방지 — 진행 중이면 추가 클릭을 무시해 PATCH 중복 호출을 막는다
    if (isSubmitting) return;
    setIsSubmitting(true);

    let result: Awaited<ReturnType<typeof UserApi.updateMarketingPolicy>>;
    try {
      result = await UserApi.updateMarketingPolicy({
        marketingPolicy: true,
        issueCoupon: true,
      });
    } catch (error) {
      datadogLogs.logger.error('마케팅 수신 동의 저장 실패', { error });
      return;
    } finally {
      setIsSubmitting(false);
    }

    // OS 알림 권한은 쿠폰 발급과 무관 — 동의 후 네이티브에 권한 요청만 위임한다 (허용은 유저 선택)
    requestNotificationPermission();

    new ReactNativeGtm().click_download_coupon({
      coupon_id: result.coupon.coupon?.couponId ?? null,
    });

    if (
      result.coupon.reason === CouponIssueReason.ISSUED &&
      result.coupon.coupon
    ) {
      setIssuedCoupon(result.coupon.coupon);
      setStep(MarketingConsentStep.COUPON_ISSUED);
      return;
    }

    // 동의 성공 + 발급 불가(이미 발급·활성 쿠폰 없음 등). eligibility로 대부분 사전 차단되는 드문 경로이며, 동의 자체는 이미 반영됐으므로 별도 완료 안내 없이 종료한다.
    // me 갱신으로 marketingPolicy=true가 반영되면 슬롯이 해제돼 모달이 닫힌다.
    startCooldown(CLOSE_COOLDOWN_DURATION_MS);
    refetchMe();
  };

  const handleClickAgreeAndGetCoupon = () => {
    void agreeMarketingAndIssueCoupon();
  };

  const handleClose = () => startCooldown(CLOSE_COOLDOWN_DURATION_MS);

  const handleNeverShowForSevenDays = () =>
    startCooldown(NEVER_SHOW_COOLDOWN_DURATION_MS);

  const handleCloseCouponIssued = () => {
    startCooldown(CLOSE_COOLDOWN_DURATION_MS);
    refetchMe();
  };

  const handleClickGoToMarket = () => {
    startCooldown(CLOSE_COOLDOWN_DURATION_MS);
    refetchMe();
    history.push(MARKET_PATH);
  };

  // 슬롯이 비활성화되면(닫힘·경로 이탈) 다음 노출이 동의 단계부터 시작하도록 초기화한다
  useEffect(() => {
    if (isActive) return;

    setStep(MarketingConsentStep.CONSENT);
    setIssuedCoupon(null);
  }, [isActive]);

  return {
    isOpen: isActive,
    step,
    issuedCoupon,
    isSubmitting,
    handleClickAgreeAndGetCoupon,
    handleClose,
    handleNeverShowForSevenDays,
    handleCloseCouponIssued,
    handleClickGoToMarket,
  };
};
